import React from "react";
import TopMenuBar from "../TopMenuBar";
import Container from "@mui/material/Container";
import { Helmet } from "react-helmet";
import { Button } from "@mui/material";
import useMyStatus from "../../services/useMyStatus";

const TenantSettings: React.FC = () => {
  const { data } = useMyStatus();
  const adminConsentUrl =
    "https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=ae2d453e-3db2-410a-966c-b229e6425534&scope=https://graph.microsoft.com/.default&redirect_uri=" +
    window.location.origin +
    "/adminconsent";

  function getUrl(): string {
    if (typeof window == "undefined") return "";
    return window.location.href;
  }

  let metaTitle = "ChangePilot Tenant Connection";
  let metaDescription = `Get the most from ChangePilot by connecting your Microsoft 365 tenant(s). By giving ChangePilot read only access to your Message Center, you will see all messages specifically for your environment. Once connected, your ChangePilot Email will also be specifically for your tenants' messages.`;
  const fallBackLogo =
    window.location.protocol +
    "//" +
    window.location.hostname +
    "/ChangePilotFaviconMint500px.png";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: metaTitle,
    alternateName: "ChangePilot from Empowering Cloud",
    url: getUrl(),
    image: fallBackLogo,
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        {metaTitle && <meta property="og:title" content={metaTitle} />}
        {metaDescription && (
          <meta property="og:description" content={metaDescription} />
        )}
        <meta property="og:type" content="website"></meta>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        {getUrl() && <meta property="og:url" content={getUrl()} />}
        <meta property="og:image" content={fallBackLogo} />
        <link rel="canonical" href={getUrl()} />
      </Helmet>
      <TopMenuBar />
      <Container maxWidth="xl" sx={{ mt: 4, height: "100vh" }}>
        <h1>Tenant Connection</h1>

        {data && !data?.isTenantIdStored ? (
          <>
            <p>
              <strong>You're currently viewing the Standard Feed</strong> — a
              feed of Microsoft 365 updates shared across multiple tenants. This
              feed will show most items but:
            </p>
            <ul>
              <li>
                May show messages about products you don't use or have licenses
                for
              </li>
              <li>
                Will miss important messages targeted by Microsoft to your
                specific tenant configuration
              </li>
            </ul>
            <div>
              <h2>Unlock the Full ChangePilot Experience</h2>
              <strong>
                Connect your Microsoft 365 tenant(s) to receive personalized
                updates.
              </strong>
            </div>
            <p>
              By granting ChangePilot read-only access to your Message Center,
              you'll:
              <ul>
                <li>See only relevant messages specific to your environment</li>
                <li>
                  Receive important messages based on your licenses and
                  configuration
                </li>
                <li>
                  Get customized ChangePilot email alerts for your tenant's
                  messages
                </li>
              </ul>
            </p>
            <p>
              Without connecting your tenant, you'll continue receiving the
              limited Standard Feed, potentially missing critical updates that
              require action in your specific environment.
            </p>
            <p>
              For more information about the read-only permissions required,
              see:{" "}
              <a
                href="https://changepilot.cloud/knowledge-base/what-permissions-does-changepilot-require-from-our-tenant-"
                target="_blank"
                rel="noreferrer noopener"
              >
                What permissions does ChangePilot require from our tenant?
              </a>
            </p>
            <p>Tenant connection requires the following permissions:</p>
            <ul>
              <li>Read Only Message Center Items</li>
              <li>Sign in and read user profile</li>
            </ul>
            <Button
              href={adminConsentUrl}
              target="_blank"
              variant="contained"
              color="primary"
            >
              View permissions and connect to M365 tenant
            </Button>{" "}
            <i>(requires tenant admin access)</i>
          </>
        ) : (
          <>
            <p>You have successfully connected to your tenant. </p>
            <p>
              Data Gathering Status:{" "}
              <strong>{data?.dataGatheringStatus}</strong>
            </p>
            <p>
              <strong>Next Steps:</strong>
              <ul>
                <li>
                  It can take up to 8 hours for data to be collected from your
                  tenant the first time.
                </li>
                <li>
                  Once collected, your ChangePilot home page will automatically
                  show data from your tenant.
                </li>
              </ul>
            </p>
          </>
        )}
      </Container>
    </>
  );
};

export default TenantSettings;
